@if (!drawerMode) {
  <app-page-breadcrumb [pageTitle]="pageTitle"></app-page-breadcrumb>
}

<ng-container *ngTemplateOutlet="drawerMode ? formBody : pageWrapper"></ng-container>

<ng-template #pageWrapper>
<div class="grid grid-cols-1 gap-6">
  <app-component-card [title]="pageTitle" desc="Complete los datos de la reunión">
    <ng-container *ngTemplateOutlet="formBody"></ng-container>
  </app-component-card>
</div>
</ng-template>

<ng-template #formBody>
  <div [class.p-6]="drawerMode">

    <!-- Loading -->
    @if (loading) {
      <div class="flex justify-center items-center py-12">
        <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-500"></div>
        <span class="ml-3 text-gray-500 dark:text-gray-400">Cargando datos...</span>
      </div>
    }

    <!-- Error -->
    @if (error) {
      <div class="mb-4 p-4 bg-error-50 border border-error-200 rounded-lg dark:bg-error-500/10 dark:border-error-500/20">
        <div class="flex items-center gap-3">
          <svg class="w-5 h-5 text-error-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
          </svg>
          <p class="text-error-600 dark:text-error-400">{{ error }}</p>
        </div>
      </div>
    }

    <!-- Formulario -->
    @if (!loading) {
      <form [formGroup]="form" (ngSubmit)="guardar()" class="space-y-5">

        <!-- Título de la reunión -->
        <div>
          <label class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
            Título de la reunión <span class="text-error-500">*</span>
          </label>
          <input
            type="text"
            formControlName="title"
            placeholder="Ej: Demo CRM, Reunión de seguimiento, etc."
            class="dark:bg-dark-900 h-11 w-full rounded-lg border bg-transparent px-4 py-2.5 text-sm text-gray-800 shadow-theme-xs placeholder:text-gray-400 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:placeholder:text-white/30 dark:focus:border-brand-800 disabled:opacity-50 disabled:cursor-not-allowed"
            [class.border-error-500]="hasError('title')"
            [class.border-gray-300]="!hasError('title')"
          />
          @if (hasError('title')) {
            <p class="mt-1.5 text-sm text-error-500">{{ getError('title') }}</p>
          }
        </div>

        <!-- Descripción -->
        <div>
          <label class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
            Descripción
          </label>
          <textarea
            formControlName="description"
            rows="3"
            placeholder="Descripción detallada de la reunión (opcional)"
            class="dark:bg-dark-900 w-full rounded-lg border bg-transparent px-4 py-2.5 text-sm text-gray-800 shadow-theme-xs placeholder:text-gray-400 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:placeholder:text-white/30 dark:focus:border-brand-800 disabled:opacity-50 disabled:cursor-not-allowed"
            [class.border-error-500]="hasError('description')"
            [class.border-gray-300]="!hasError('description')"
          ></textarea>
          <div class="mt-1.5 flex items-center justify-between">
            @if (hasError('description')) {
              <p class="text-sm text-error-500">La descripción supera el máximo de 2000 caracteres</p>
            } @else {
              <span></span>
            }
            <span class="text-xs"
              [class.text-error-500]="fieldLength('description') > 2000"
              [class.text-gray-400]="fieldLength('description') <= 2000">
              {{ fieldLength('description') }}/2000
            </span>
          </div>
        </div>

        <!-- ========== Fecha y hora ========== -->
        <div class="pt-4 border-t border-gray-200 dark:border-gray-700">
          <h4 class="text-sm font-semibold text-gray-600 dark:text-gray-400 mb-4">Fecha y hora</h4>

          <div class="grid grid-cols-1 gap-5 sm:grid-cols-2">
            <!-- Fecha y hora inicio -->
            <div>
              <div class="mb-1.5 flex items-center justify-between">
                <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Fecha y hora inicio <span class="text-error-500">*</span>
                </label>
                <button
                  type="button"
                  (click)="setStartNow()"
                  class="text-xs font-medium text-brand-600 hover:text-brand-700 dark:text-brand-400">
                  Reunión ahora
                </button>
              </div>
              <input
                type="datetime-local"
                formControlName="datetimeStart"
                [attr.min]="isEditing ? null : minDatetimeStart"
                class="dark:bg-dark-900 h-11 w-full rounded-lg border bg-transparent px-4 py-2.5 text-sm text-gray-800 shadow-theme-xs focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:focus:border-brand-800 disabled:opacity-50 disabled:cursor-not-allowed"
                [class.border-error-500]="hasError('datetimeStart')"
                [class.border-gray-300]="!hasError('datetimeStart')"
              />
              @if (hasError('datetimeStart')) {
                <p class="mt-1.5 text-sm text-error-500">{{ getError('datetimeStart') }}</p>
              }
            </div>

            <!-- Duración (en minutos) — datetimeEnd se calcula automáticamente -->
            <div>
              <label class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
                Duración (minutos) <span class="text-error-500">*</span>
              </label>
              <input
                type="number"
                min="5"
                max="1440"
                step="5"
                formControlName="durationMinutes"
                placeholder="60"
                class="dark:bg-dark-900 h-11 w-full rounded-lg border bg-transparent px-4 py-2.5 text-sm text-gray-800 shadow-theme-xs focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:focus:border-brand-800"
                [class.border-error-500]="hasError('durationMinutes')"
                [class.border-gray-300]="!hasError('durationMinutes')" />
              <div class="mt-2 flex flex-wrap gap-1.5">
                @for (preset of durationPresets; track preset.value) {
                  <button
                    type="button"
                    (click)="setDuration(preset.value)"
                    class="rounded-md border px-2 py-0.5 text-xs transition-colors"
                    [class.border-brand-500]="form.get('durationMinutes')?.value === preset.value"
                    [class.bg-brand-50]="form.get('durationMinutes')?.value === preset.value"
                    [class.text-brand-700]="form.get('durationMinutes')?.value === preset.value"
                    [class.dark:bg-brand-900/30]="form.get('durationMinutes')?.value === preset.value"
                    [class.dark:text-brand-400]="form.get('durationMinutes')?.value === preset.value"
                    [class.border-gray-300]="form.get('durationMinutes')?.value !== preset.value"
                    [class.text-gray-600]="form.get('durationMinutes')?.value !== preset.value"
                    [class.dark:border-gray-700]="form.get('durationMinutes')?.value !== preset.value"
                    [class.dark:text-gray-400]="form.get('durationMinutes')?.value !== preset.value">
                    {{ preset.label }}
                  </button>
                }
              </div>
              @if (computedEndPreview) {
                <p class="mt-1.5 text-xs text-gray-500 dark:text-gray-400">
                  Termina: <span class="font-medium text-gray-700 dark:text-gray-300">{{ computedEndPreview }}</span>
                </p>
              }
              @if (hasError('durationMinutes')) {
                <p class="mt-1.5 text-sm text-error-500">{{ getError('durationMinutes') }}</p>
              }
            </div>
          </div>
        </div>

        <!-- ========== Página de reservas (INC 26) — solo en creación ========== -->
        @if (!isEditing) {
          <div class="pt-4 border-t border-gray-200 dark:border-gray-700">
            <label class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
              Página de reservas (opcional)
            </label>
            <select
              [(ngModel)]="selectedBookingLinkId"
              [ngModelOptions]="{ standalone: true }"
              (ngModelChange)="onBookingLinkChange()"
              class="dark:bg-dark-900 h-11 w-full rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 text-sm text-gray-800 shadow-theme-xs focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:focus:border-brand-800"
            >
              <option [ngValue]="null">Sin página de reservas</option>
              @for (link of bookingLinks; track link.id) {
                <option [ngValue]="link.id">{{ bookingLinkLabel(link) }}</option>
              }
            </select>
            @if (loadingBookingLinks) {
              <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">Cargando páginas de reservas...</p>
            } @else if (selectedBookingLinkId) {
              <p class="mt-1.5 text-xs text-gray-500 dark:text-gray-400">
                Los campos vacíos (duración, tipo, zona horaria, video, agenda) se completarán automáticamente con la configuración de la página al guardar.
              </p>
            }
          </div>
        }

        <!-- ========== Ubicación presencial (INC 40 + INC 17) ========== -->
        <!-- INC 40 — para reuniones virtuales usa "Canal de video" en Opciones
             avanzadas; presencial (ubicación) y virtual (enlace) son excluyentes. -->
        <div class="pt-4 border-t border-gray-200 dark:border-gray-700">
          <h4 class="text-sm font-semibold text-gray-600 dark:text-gray-400 mb-1">Ubicación (presencial)</h4>
          <p class="text-xs text-gray-400 dark:text-gray-500 mb-4">
            ¿Reunión virtual? Elige el canal de video en “Opciones avanzadas”. La ubicación y el enlace son excluyentes.
          </p>

          <!-- Modalidad obligatoria: sin lugar y sin canal de video no se puede
               guardar (y el cliente no recibiría el correo de confirmación). -->
          @if (showModalidadError) {
            <div id="meeting-modalidad-error"
                 class="mb-4 flex items-start gap-3 rounded-lg border border-error-300 bg-error-50 p-3 dark:border-error-500/30 dark:bg-error-500/10">
              <svg class="mt-0.5 h-5 w-5 shrink-0 text-error-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/>
              </svg>
              <div class="min-w-0">
                <p class="text-sm font-medium text-error-600 dark:text-error-400">Falta la modalidad de la reunión</p>
                <p class="mt-0.5 text-sm text-error-600 dark:text-error-400">{{ modalidadError }}</p>
                <p class="mt-1 text-xs text-gray-600 dark:text-gray-400">
                  Escribe la dirección aquí abajo (presencial) o elige un canal de video en “Opciones avanzadas” (virtual).
                </p>
              </div>
            </div>
          }

          <div class="grid grid-cols-1 gap-5 sm:grid-cols-2">
            <!-- Ubicación física -->
            <div>
              <label class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
                Dirección / Lugar
              </label>
              <input
                type="text"
                formControlName="location"
                placeholder="Ej: Oficina cliente, Sala de reuniones, etc."
                class="dark:bg-dark-900 h-11 w-full rounded-lg border bg-transparent px-4 py-2.5 text-sm text-gray-800 shadow-theme-xs placeholder:text-gray-400 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/10 dark:bg-gray-900 dark:text-white/90 dark:placeholder:text-white/30 dark:focus:border-brand-800 disabled:opacity-50 disabled:cursor-not-allowed"
                [class.border-error-500]="showModalidadError || hasError('location')"
                [class.border-gray-300]="!showModalidadError && !hasError('location')"
                [class.dark:border-gray-700]="!showModalidadError && !hasError('location')"
              />
              @if (hasError('location')) {
                <p class="mt-1.5 text-sm text-error-500">{{ getError('location') }}</p>
              }
            </div>

            <!-- Enlace de Google Maps (INC 17) -->
            <div>
              <label class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
                Enlace de Google Maps
              </label>
              <input
                type="url"
                formControlName="locationMapUrl"
                placeholder="https://maps.google.com/..."
                class="dark:bg-dark-900 h-11 w-full rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 text-sm text-gray-800 shadow-theme-xs placeholder:text-gray-400 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:placeholder:text-white/30 dark:focus:border-brand-800 disabled:opacity-50 disabled:cursor-not-allowed"
              />
              @if (hasError('locationMapUrl')) {
                <p class="mt-1.5 text-sm text-error-500">{{ getError('locationMapUrl') }}</p>
              }
            </div>
          </div>
        </div>

        <!-- ========== Tipo y estado ========== -->
        <div class="grid grid-cols-1 gap-5 sm:grid-cols-2">
          <!-- Tipo de reunión -->
          <div>
            <label class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
              Tipo de reunión
            </label>
            <select
              formControlName="idMeetingType"
              (change)="onMeetingTypeChange()"
              class="dark:bg-dark-900 h-11 w-full rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 text-sm text-gray-800 shadow-theme-xs focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:focus:border-brand-800 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <option [ngValue]="null">Seleccionar tipo</option>
              @for (type of meetingTypes; track type.id) {
              <option [ngValue]="type.id">{{ type.name }}</option>
              }
            </select>
            @if (loadingTypes) {
              <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">Cargando tipos...</p>
            }
            @if (hasError('idMeetingType')) {
              <p class="mt-1.5 text-sm text-error-500">{{ getError('idMeetingType') }}</p>
            }
          </div>

          <!-- Estado de reunión -->
          <div>
            <label class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
              Estado
            </label>
            <select
              formControlName="idMeetingStatus"
              class="dark:bg-dark-900 h-11 w-full rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 text-sm text-gray-800 shadow-theme-xs focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:focus:border-brand-800 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <option [ngValue]="null">Seleccionar estado</option>
              @for (status of meetingStatuses; track status.id) {
              <option [ngValue]="status.id">{{ status.name }}</option>
              }
            </select>
            @if (loadingStatuses) {
              <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">Cargando estados...</p>
            }
            @if (hasError('idMeetingStatus')) {
              <p class="mt-1.5 text-sm text-error-500">{{ getError('idMeetingStatus') }}</p>
            }
          </div>
        </div>

        <!-- ========== Relaciones ========== -->
        <div class="pt-4 border-t border-gray-200 dark:border-gray-700">
  <h4 class="text-sm font-semibold text-gray-600 dark:text-gray-400 mb-4">Relaciones</h4>

  <div class="grid grid-cols-1 gap-5 sm:grid-cols-3">
    
    <!-- Contacto typeahead -->
    <div>
      <div class="flex items-center gap-2 mb-1.5">
        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">
          Contacto <span class="text-xs font-normal text-gray-400 dark:text-gray-500">(opcional)</span>
        </label>
        @if (contactAutoFilled) {
          <span class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-success-100 text-success-700 dark:bg-success-500/20 dark:text-success-400 animate-pulse">
            <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
            </svg>
            Auto-completado
          </span>
        }
      </div>
      <div class="relative">
        <input
          type="text"
          [value]="contactSearchTerm"
          (input)="onContactSearchInput($any($event.target).value)"
          (keydown.enter)="onContactSearchEnter($event)"
          (blur)="closeContactDropdown()"
          autocomplete="off"
          placeholder="Buscar contacto... (Enter para ver todos)"
          class="dark:bg-dark-900 h-11 w-full rounded-lg border bg-transparent px-4 py-2.5 text-sm text-gray-800 shadow-theme-xs placeholder:text-gray-400 focus:outline-hidden focus:ring-3 dark:text-white/90 dark:placeholder:text-white/30 transition-all duration-500"
          [class.border-success-400]="contactAutoFilled"
          [class.bg-success-50]="contactAutoFilled"
          [class.ring-success-500\/20]="contactAutoFilled"
          [class.focus:border-success-400]="contactAutoFilled"
          [class.border-gray-300]="!contactAutoFilled && !(!idContact && name)"
          [class.border-error-300]="!contactAutoFilled && !idContact && !!name"
          [class.focus:border-brand-300]="!contactAutoFilled"
          [class.focus:ring-brand-500\/10]="!contactAutoFilled"
          [class.dark:border-gray-700]="!contactAutoFilled"
          [class.dark:bg-gray-900]="!contactAutoFilled"
        />
        @if (contactSearchTerm) {
          <button 
            type="button" 
            (click)="clearContact()"
            class="absolute right-3 top-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 z-10"
          >
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </button>
        }

        @if (searchingContacts) {
          <div class="absolute right-3 top-3.5">
            <div class="animate-spin rounded-full h-4 w-4 border-b-2 border-brand-500"></div>
          </div>
        }

         @if (showContactDropdown && contactSearchResults.length > 0) {
          <div 
            class="absolute z-10 mt-1 w-full rounded-lg border border-gray-200 bg-white shadow-lg dark:border-gray-700 dark:bg-gray-900 max-h-48 overflow-y-auto"
            (scroll)="onContactDropdownScroll($event)"
          >
            @for (c of contactSearchResults; track c.id) {
              <button 
                type="button" 
                (mousedown)="selectContact(c)"
                class="w-full px-4 py-2.5 text-left hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
              >
                <span class="block text-sm font-medium text-gray-900 dark:text-white">
                  {{ c.fullname }} {{ c.lastname || '' }}
                </span>
                <span class="block text-xs text-gray-500 dark:text-gray-400">{{ c.mail }}</span>
              </button>
            }
            @if (loadingMoreContacts) {
              <div class="flex justify-center items-center py-2">
                <div class="animate-spin rounded-full h-4 w-4 border-b-2 border-brand-500"></div>
                <span class="ml-2 text-xs text-gray-400 dark:text-gray-500">Cargando más...</span>
              </div>
            }
          </div>
        }
      </div>
    </div>

    <!-- ID Negocio/Deal -->
    <div>
    <div class="flex items-center gap-2 mb-1.5">
      <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">
        Negocio <span class="text-xs font-normal text-gray-400 dark:text-gray-500">(opcional)</span>
      </label>
      @if (dealAutoFilled) {
        <span class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-success-100 text-success-700 dark:bg-success-500/20 dark:text-success-400 animate-pulse">
          <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
          </svg>
          Auto-completado
        </span>
      }
    </div>
    <div class="relative">
      <input
        type="text"
        [value]="dealSearchTerm"
        (input)="onDealSearchInput($any($event.target).value)"
        (keydown.enter)="onDealSearchEnter($event)"
        (blur)="closeDealDropdown()"
        autocomplete="off"
        placeholder="Buscar negocio... (Enter para ver todos)"
        class="dark:bg-dark-900 h-11 w-full rounded-lg border bg-transparent px-4 py-2.5 text-sm text-gray-800 shadow-theme-xs placeholder:text-gray-400 focus:outline-hidden focus:ring-3 dark:text-white/90 dark:placeholder:text-white/30 transition-all duration-500"
        [class.border-success-400]="dealAutoFilled"
        [class.bg-success-50]="dealAutoFilled"
        [class.ring-success-500\/20]="dealAutoFilled"
        [class.focus:border-success-400]="dealAutoFilled"
        [class.border-gray-300]="!dealAutoFilled"
        [class.focus:border-brand-300]="!dealAutoFilled"
        [class.focus:ring-brand-500\/10]="!dealAutoFilled"
        [class.dark:border-gray-700]="!dealAutoFilled"
        [class.dark:bg-gray-900]="!dealAutoFilled"
      />
       @if (dealSearchTerm && !searchingDeals) {
        <button 
          type="button" 
          (click)="clearDeal()"
          class="absolute right-3 top-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
        >
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
          </svg>
        </button>
      }
      @if (searchingDeals) {
        <div class="absolute right-3 top-3.5">
          <div class="animate-spin rounded-full h-4 w-4 border-b-2 border-brand-500"></div>
        </div>
      }
  
      @if (showDealDropdown && dealSearchResults.length > 0) {
        <div 
          class="absolute z-10 mt-1 w-full rounded-lg border border-gray-200 bg-white shadow-lg dark:border-gray-700 dark:bg-gray-900 max-h-48 overflow-y-auto"
          (scroll)="onDealDropdownScroll($event)"
        >
          @for (deal of dealSearchResults; track deal.id) {
            <button 
              type="button" 
              (mousedown)="selectDeal(deal)"
              class="w-full px-4 py-2.5 text-left hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
            >
              <span class="block text-sm font-medium text-gray-900 dark:text-white">
                {{ deal.name || deal.title || 'Sin título' }}
              </span>
              @if (deal.amount) {
                <span class="block text-xs text-gray-500 dark:text-gray-400">
                  Monto: {{ deal.amount | currency }}
                </span>
              }
            </button>
          }
          @if (loadingMoreDeals) {
            <div class="flex justify-center items-center py-2">
              <div class="animate-spin rounded-full h-4 w-4 border-b-2 border-brand-500"></div>
              <span class="ml-2 text-xs text-gray-400 dark:text-gray-500">Cargando más...</span>
            </div>
          }
        </div>
      }
    </div>
  </div>

    <!-- Empresa typeahead -->
    <div>
      <div class="flex items-center gap-2 mb-1.5">
        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">
          Empresa <span class="text-xs font-normal text-gray-400 dark:text-gray-500">(opcional)</span>
        </label>
        @if (companyAutoFilled) {
          <span class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-success-100 text-success-700 dark:bg-success-500/20 dark:text-success-400 animate-pulse">
            <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
            </svg>
            Auto-completado
          </span>
        }
      </div>
      <div class="relative">
        <input 
          type="text" 
          [value]="companySearchTerm" 
          (input)="onCompanySearchInput($any($event.target).value)"
          (keydown.enter)="onCompanySearchEnter($event)" 
          (blur)="closeCompanyDropdown()" 
          autocomplete="off"
          placeholder="Buscar empresa... (Enter para ver todas)"
          class="dark:bg-dark-900 h-11 w-full rounded-lg border bg-transparent px-4 py-2.5 text-sm text-gray-800 shadow-theme-xs placeholder:text-gray-400 focus:outline-hidden focus:ring-3 dark:text-white/90 dark:placeholder:text-white/30 transition-all duration-500"
          [class.border-success-400]="companyAutoFilled" 
          [class.bg-success-50]="companyAutoFilled"
          [class.ring-success-500\/20]="companyAutoFilled" 
          [class.focus:border-success-400]="companyAutoFilled"
          [class.border-gray-300]="!companyAutoFilled && !(!idCompany && name)"
          [class.border-error-300]="!companyAutoFilled && !idCompany && !!name"
          [class.focus:border-brand-300]="!companyAutoFilled" 
          [class.focus:ring-brand-500\/10]="!companyAutoFilled"
          [class.dark:border-gray-700]="!companyAutoFilled" 
          [class.dark:bg-gray-900]="!companyAutoFilled" 
        />
        @if (companySearchTerm) {
          <button 
            type="button" 
            (click)="clearCompany()"
            class="absolute right-3 top-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </button>
        }
        @if (searchingCompanies) {
          <div class="absolute right-3 top-3.5">
            <div class="animate-spin rounded-full h-4 w-4 border-b-2 border-brand-500"></div>
          </div>
        }
        @if (showCompanyDropdown && companySearchResults.length > 0) {
          <div 
            class="absolute z-10 mt-1 w-full rounded-lg border border-gray-200 bg-white shadow-lg dark:border-gray-700 dark:bg-gray-900 max-h-48 overflow-y-auto"
            (scroll)="onCompanyDropdownScroll($event)"
          >
            @for (c of companySearchResults; track c.id) {
              <button 
                type="button" 
                (mousedown)="selectCompany(c)"
                class="w-full px-4 py-2.5 text-left hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
              >
                <span class="block text-sm font-medium text-gray-900 dark:text-white">
                  {{ c.commercialName }}
                </span>
                @if (c.businessName) {
                  <span class="block text-xs text-gray-500 dark:text-gray-400">{{ c.businessName }}</span>
                }
              </button>
            }
            @if (loadingMoreCompanies) {
              <div class="flex justify-center items-center py-2">
                <div class="animate-spin rounded-full h-4 w-4 border-b-2 border-brand-500"></div>
                <span class="ml-2 text-xs text-gray-400 dark:text-gray-500">Cargando más...</span>
              </div>
            }
          </div>
        }
      </div>
    </div>
  </div>


          <!-- Organizador -->
          <div class="mt-5">
            <label class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
              Organizador
            </label>
            <div class="h-11 w-full rounded-lg border border-gray-300 dark:border-gray-700 bg-gray-100 dark:bg-gray-800 px-4 py-2.5 text-sm text-gray-800 dark:text-white/90 flex items-center">
            {{ ownerDisplayName }}
          </div>
            @if (hasError('organizerId')) {
              <p class="mt-1.5 text-sm text-error-500">{{ getError('organizerId') }}</p>
            }
          </div>
        </div>

        <!-- ========== Participantes internos (INC 41) ========== -->
        <div class="pt-4 border-t border-gray-200 dark:border-gray-700">
          <h4 class="text-sm font-semibold text-gray-600 dark:text-gray-400 mb-1">Participantes internos</h4>
          <p class="text-xs text-gray-400 dark:text-gray-500 mb-4">
            Usuarios del sistema invitados a la reunión. Recibirán notificación y correo. El organizador no se incluye.
          </p>

          <div class="flex flex-col gap-2 sm:flex-row sm:items-start">
            <div class="flex-1">
              <app-entity-select
                [value]="newInternalUserId"
                [displayText]="newInternalUserName"
                (selected)="onInternalUserPicked($any($event))"
                [searchFn]="userSearch"
                [labelFn]="userLabel"
                [sublabelFn]="userSublabel"
                placeholder="Buscar usuario por nombre o correo…">
              </app-entity-select>
            </div>
            <app-button variant="outline" type="button" (btnClick)="addInternalParticipant()" [disabled]="!newInternalUserId">
              Agregar
            </app-button>
          </div>

          @if (internalParticipants.length) {
            <div class="mt-3 flex flex-wrap gap-2">
              @for (p of internalParticipants; track p.userId) {
                <span class="inline-flex items-center gap-1.5 rounded-full bg-brand-50 px-3 py-1 text-sm font-medium text-brand-700 dark:bg-brand-500/10 dark:text-brand-400">
                  {{ p.name }}
                  <button type="button" (click)="removeInternalParticipant(p.userId)"
                    class="text-brand-500 hover:text-brand-700 dark:hover:text-brand-300" title="Quitar">
                    <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
                    </svg>
                  </button>
                </span>
              }
            </div>
          } @else {
            <p class="mt-3 text-xs text-gray-400 dark:text-gray-500">Sin participantes internos.</p>
          }
        </div>

        <!-- ========== Notas ========== -->
        <div class="pt-4 border-t border-gray-200 dark:border-gray-700">
          <h4 class="text-sm font-semibold text-gray-600 dark:text-gray-400 mb-4">Notas</h4>

          <div class="grid grid-cols-1 gap-5">
            <!-- Notas — sin etiqueta propia: la sección ya se titula «Notas» y es su
                 único campo, así que la palabra salía dos veces seguidas. -->
            <div>
              <textarea
                formControlName="notes"
                aria-label="Notas"
                rows="3"
                placeholder="Notas adicionales sobre la reunión (preparación, agenda, etc.)"
                class="dark:bg-dark-900 w-full rounded-lg border bg-transparent px-4 py-2.5 text-sm text-gray-800 shadow-theme-xs placeholder:text-gray-400 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:placeholder:text-white/30 dark:focus:border-brand-800 disabled:opacity-50 disabled:cursor-not-allowed"
                [class.border-error-500]="hasError('notes')"
                [class.border-gray-300]="!hasError('notes')"
              ></textarea>
              <div class="mt-1.5 flex items-center justify-between">
                @if (hasError('notes')) {
                  <p class="text-sm text-error-500">Las notas superan el máximo de 2000 caracteres</p>
                } @else {
                  <span></span>
                }
                <span class="text-xs"
                  [class.text-error-500]="fieldLength('notes') > 2000"
                  [class.text-gray-400]="fieldLength('notes') <= 2000">
                  {{ fieldLength('notes') }}/2000
                </span>
              </div>
            </div>
          </div>
        </div>

        <!-- ─── Sección Avanzado (B13) ───────────────────────────── -->
        <div class="border-t border-gray-200 dark:border-gray-700 pt-4">
          <button
            type="button"
            (click)="toggleAdvanced()"
            class="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-brand-500 transition-colors"
          >
            <svg class="w-4 h-4 transition-transform" [class.rotate-90]="showAdvanced" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
            </svg>
            Opciones avanzadas (origen, canal de video, zona horaria, GDPR)
          </button>

          @if (showAdvanced) {
          <div class="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
            <!-- Origen -->
            <div>
              <label class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">Origen</label>
              <select formControlName="source"
                class="dark:bg-dark-900 w-full rounded-lg border border-gray-300 bg-transparent px-3 py-2 text-sm text-gray-800 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90">
                <option [ngValue]="null">— Sin especificar —</option>
                <option value="manual">Manual</option>
                <option value="booking_page">Booking page</option>
                <option value="api">API</option>
                <option value="import">Import</option>
              </select>
            </div>

            <!-- Video provider — picker visual de tarjetas (Task 3) -->
            <div class="md:col-span-2">
              <label class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">Canal de video</label>
              <!-- Control real (valor lo maneja el picker de abajo) -->
              <input type="hidden" formControlName="videoProvider" />

              <div role="radiogroup" aria-label="Canal de video"
                class="grid grid-cols-2 gap-2 sm:grid-cols-4">
                @for (opt of videoProviderOptions; track opt.label) {
                  <button
                    type="button"
                    role="radio"
                    [attr.aria-checked]="isVideoProviderSelected(opt.value)"
                    (click)="setVideoProvider(opt.value)"
                    class="group flex flex-col items-center justify-center gap-1.5 rounded-lg border px-2 py-3 text-center transition-colors focus:outline-hidden focus:ring-3 focus:ring-brand-500/10"
                    [class.border-brand-500]="isVideoProviderSelected(opt.value)"
                    [class.bg-brand-50]="isVideoProviderSelected(opt.value)"
                    [class.dark:bg-brand-500/10]="isVideoProviderSelected(opt.value)"
                    [class.dark:border-brand-500]="isVideoProviderSelected(opt.value)"
                    [class.border-gray-300]="!isVideoProviderSelected(opt.value)"
                    [class.dark:border-gray-700]="!isVideoProviderSelected(opt.value)"
                    [class.hover:border-brand-300]="!isVideoProviderSelected(opt.value)">
                    <span
                      class="flex h-8 w-8 items-center justify-center"
                      [class.text-brand-600]="isVideoProviderSelected(opt.value)"
                      [class.dark:text-brand-400]="isVideoProviderSelected(opt.value)"
                      [class.text-gray-500]="!isVideoProviderSelected(opt.value)"
                      [class.dark:text-gray-400]="!isVideoProviderSelected(opt.value)">
                      @switch (opt.value) {
                        @case (null) {
                          <!-- Sin video / presencial: pin de ubicación -->
                          <svg class="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.8" d="M17.657 16.657L13.414 20.9a2 2 0 01-2.828 0l-4.243-4.243a8 8 0 1111.314 0z"/>
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.8" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/>
                          </svg>
                        }
                        @case ('jitsi') {
                          <!-- Jitsi: cámara de video -->
                          <svg class="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.8" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"/>
                          </svg>
                        }
                        @case ('google') {
                          <!-- Google Meet: video con destello -->
                          <svg class="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.8" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14"/>
                            <rect x="3" y="6" width="12" height="12" rx="2" stroke-width="1.8"/>
                          </svg>
                        }
                        @case ('microsoft') {
                          <!-- Microsoft Teams: personas / grupo -->
                          <svg class="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.8" d="M17 20h5v-2a4 4 0 00-3-3.87M9 20H4v-2a4 4 0 013-3.87m6-2.13a4 4 0 10-4-4 4 4 0 004 4z"/>
                          </svg>
                        }
                        @case ('zoom') {
                          <!-- Zoom: cámara redonda -->
                          <svg class="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <rect x="3" y="6" width="13" height="12" rx="3" stroke-width="1.8"/>
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.8" d="M16 10l4.553-2.276A1 1 0 0122 8.618v6.764a1 1 0 01-1.447.894L16 14"/>
                          </svg>
                        }
                        @case ('webex') {
                          <!-- Webex: círculo de llamada -->
                          <svg class="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <circle cx="12" cy="12" r="9" stroke-width="1.8"/>
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.8" d="M8.5 9.5a5 5 0 007 5"/>
                          </svg>
                        }
                        @default {
                          <!-- Otro: enlace genérico -->
                          <svg class="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.8" d="M13.828 10.172a4 4 0 010 5.656l-3 3a4 4 0 01-5.656-5.656l1.5-1.5M10.172 13.828a4 4 0 010-5.656l3-3a4 4 0 015.656 5.656l-1.5 1.5"/>
                          </svg>
                        }
                      }
                    </span>
                    <span class="text-xs font-medium leading-tight"
                      [class.text-brand-700]="isVideoProviderSelected(opt.value)"
                      [class.dark:text-brand-400]="isVideoProviderSelected(opt.value)"
                      [class.text-gray-700]="!isVideoProviderSelected(opt.value)"
                      [class.dark:text-gray-300]="!isVideoProviderSelected(opt.value)">
                      {{ opt.label }}
                    </span>
                    @if (opt.auto) {
                      <span class="rounded-full bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium text-gray-500 dark:bg-gray-800 dark:text-gray-400">
                        Link automático
                      </span>
                    }
                  </button>
                }
              </div>

              @if (showModalidadError) {
                <p class="mt-1.5 text-sm text-error-500">
                  Elige aquí un canal de video para que la reunión sea virtual, o indica la dirección en “Ubicación (presencial)”.
                </p>
              }
              @if (videoProviderWarning) {
                <p class="mt-1.5 text-xs text-yellow-600 dark:text-yellow-400">⚠ {{ videoProviderWarning }}</p>
              }
              @if (isCurrentProviderAuto && !videoProviderWarning) {
                <p class="mt-1.5 text-xs text-gray-500 dark:text-gray-400">
                  El link se generará automáticamente al guardar la reunión.
                </p>
              }
            </div>

            <!-- INC 40 — enlace único de la reunión: solo para providers manuales
                 (zoom/webex/other). Mapea a meetingUrl. Los automáticos lo generan. -->
            @if (form.get('videoProvider')?.value && !isCurrentProviderAuto) {
              <div class="md:col-span-2">
                <label class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">Enlace de la reunión (URL)</label>
                <input type="url" formControlName="meetingUrl" placeholder="https://..."
                  class="dark:bg-dark-900 w-full rounded-lg border bg-transparent px-3 py-2 text-sm text-gray-800 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90"
                  [class.border-error-500]="hasError('meetingUrl')"
                  [class.border-gray-300]="!hasError('meetingUrl')">
                @if (hasError('meetingUrl')) {
                  <p class="mt-1.5 text-sm text-error-500">{{ getError('meetingUrl') }}</p>
                }
              </div>
            }

            <!-- Timezone del invitado -->
            <div>
              <label class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">Zona horaria (invitado)</label>
              <select formControlName="inviteeTimezone"
                class="dark:bg-dark-900 w-full rounded-lg border bg-transparent px-3 py-2 text-sm text-gray-800 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90"
                [class.border-error-500]="hasError('inviteeTimezone')"
                [class.border-gray-300]="!hasError('inviteeTimezone')">
                <option value="">— Selecciona zona horaria —</option>
                @if (form.get('inviteeTimezone')?.value && !timezones.includes(form.get('inviteeTimezone')?.value)) {
                  <option [value]="form.get('inviteeTimezone')?.value">{{ form.get('inviteeTimezone')?.value }} (valor guardado)</option>
                }
                @for (tz of timezones; track tz) {
                  <option [value]="tz">{{ tz }}</option>
                }
              </select>
              @if (hasError('inviteeTimezone')) {
                <p class="mt-1.5 text-sm text-error-500">{{ getError('inviteeTimezone') }}</p>
              }
            </div>

            <!-- Timezone base -->
            <div>
              <label class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">Zona horaria (reunión)</label>
              <select formControlName="timezone" (change)="onTimezoneManualChange()"
                class="dark:bg-dark-900 w-full rounded-lg border bg-transparent px-3 py-2 text-sm text-gray-800 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90"
                [class.border-error-500]="hasError('timezone')"
                [class.border-gray-300]="!hasError('timezone')">
                <option value="">— Selecciona zona horaria —</option>
                @if (form.get('timezone')?.value && !timezones.includes(form.get('timezone')?.value)) {
                  <option [value]="form.get('timezone')?.value">{{ form.get('timezone')?.value }} (valor guardado)</option>
                }
                @for (tz of timezones; track tz) {
                  <option [value]="tz">{{ tz }}</option>
                }
              </select>
              @if (timezoneAutoDetected && form.get('timezone')?.value === detectedTimezone) {
                <p class="mt-1.5 text-xs text-gray-500 dark:text-gray-400">
                  Detectada automáticamente: <span class="font-medium text-gray-700 dark:text-gray-300">{{ detectedTimezone }}</span>
                </p>
              }
              @if (hasError('timezone')) {
                <p class="mt-1.5 text-sm text-error-500">{{ getError('timezone') }}</p>
              }
            </div>

            <!-- GDPR -->
            <div class="flex items-center gap-3">
              <input id="gdprConsentGiven" type="checkbox" formControlName="gdprConsentGiven"
                class="h-4 w-4 rounded border-gray-300 text-brand-500 focus:ring-brand-500">
              <label for="gdprConsentGiven" class="text-sm text-gray-700 dark:text-gray-300">Consentimiento GDPR otorgado</label>
            </div>

            <div>
              <label class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">Fecha de consentimiento GDPR</label>
              <input type="datetime-local" formControlName="gdprConsentDatetime"
                class="dark:bg-dark-900 w-full rounded-lg border border-gray-300 bg-transparent px-3 py-2 text-sm text-gray-800 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90">
            </div>
          </div>
          }
        </div>

        <!-- Botones de acción -->
        <div class="flex items-center justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
          <app-button
            variant="outline"
            type="button"
            (btnClick)="cancelar()"
          >
            Cancelar
          </app-button>
          <!-- Solo se bloquea durante el guardado: con el form inválido el click
               dispara markAllAsTouched() y muestra los errores de cada campo -->
          <app-button
            variant="primary"
            type="submit"
            [disabled]="saving"
          >
            {{ saving ? 'Guardando...' : (isEditing ? 'Actualizar Reunión' : 'Crear Reunión') }}
          </app-button>
        </div>
      </form>
    }
  </div>
</ng-template>

<!-- Modal post-guardado: link de video generado y/o avisos persistentes
     (enlace de video no generado, correo de confirmación NO enviado).
     Es un modal y no un toast porque el usuario DEBE enterarse: el cliente
     puede haberse quedado sin su correo de confirmación. -->
@if (linkInfoVisible) {
  <div class="fixed inset-0 z-modal flex items-center justify-center bg-black/50 px-4">
    <div class="w-full max-w-md rounded-xl bg-white p-6 shadow-2xl dark:bg-gray-900">
      <div class="mb-4 flex items-start gap-3">
        @if (postSaveWarnings.length) {
          <div class="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-warning-50 text-warning-500 dark:bg-warning-500/15 dark:text-warning-400">
            <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/>
            </svg>
          </div>
        } @else {
          <div class="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400">
            <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
            </svg>
          </div>
        }
        <div class="min-w-0">
          <h3 class="text-base font-semibold text-gray-900 dark:text-white">{{ postSaveTitle }}</h3>
          @if (linkInfoUrl) {
            <p class="mt-0.5 text-sm text-gray-500 dark:text-gray-400">
              Link de {{ linkInfoProvider }} generado automáticamente.
            </p>
          } @else {
            <p class="mt-0.5 text-sm text-gray-500 dark:text-gray-400">
              La reunión se guardó correctamente, pero revisa lo siguiente:
            </p>
          }
        </div>
      </div>

      @if (linkInfoUrl) {
        <div class="mb-4 rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-gray-700 dark:bg-gray-800">
          <a [href]="linkInfoUrl" target="_blank"
             class="block break-all text-sm font-medium text-brand-600 hover:text-brand-700 dark:text-brand-400">
            {{ linkInfoUrl }}
          </a>
        </div>
      }

      @if (postSaveWarnings.length) {
        <div class="mb-4 space-y-3">
          @for (w of postSaveWarnings; track w.title) {
            <div class="rounded-lg border border-warning-200 bg-warning-50 p-3 dark:border-warning-500/30 dark:bg-warning-500/10">
              <p class="text-sm font-medium text-warning-600 dark:text-warning-400">{{ w.title }}</p>
              <p class="mt-0.5 text-sm text-gray-600 dark:text-gray-300">{{ w.message }}</p>
            </div>
          }
        </div>
      }

      <div class="flex items-center justify-end gap-2">
        @if (linkInfoUrl) {
          <button type="button" (click)="copyLinkInfo()"
                  class="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700">
            {{ linkCopied ? '¡Copiado!' : 'Copiar link' }}
          </button>
        }
        <button type="button" (click)="closeLinkInfo()"
                class="rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white hover:bg-brand-600">
          Entendido
        </button>
      </div>
    </div>
  </div>
}

<!-- INC 39 — selector de relación: aparece cuando el autocompletado encuentra MÁS
     DE UN candidato (contacto con varias empresas o negocios, empresa con varios
     negocios, negocio con varios contactos). Antes se elegía el principal / el
     último creado en silencio. -->
@if (relationChoice; as choice) {
  <div class="fixed inset-0 z-modal flex items-center justify-center bg-black/50 px-4">
    <div class="w-full max-w-md rounded-xl bg-white p-6 shadow-2xl dark:bg-gray-900">
      <div class="mb-4 flex items-start gap-3">
        <div class="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-brand-50 text-brand-500 dark:bg-brand-500/15 dark:text-brand-400">
          <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7h12M8 12h12M8 17h12M4 7h.01M4 12h.01M4 17h.01"/>
          </svg>
        </div>
        <div class="min-w-0">
          <h3 class="text-base font-semibold text-gray-900 dark:text-white">{{ choice.title }}</h3>
          @if (choice.hint) {
            <p class="mt-0.5 text-sm text-gray-500 dark:text-gray-400">{{ choice.hint }}</p>
          }
        </div>
      </div>

      <div class="max-h-72 space-y-2 overflow-y-auto">
        @for (opt of choice.options; track opt.id) {
          <button type="button" (click)="chooseRelationOption(opt)"
                  class="w-full rounded-lg border border-gray-200 bg-white px-4 py-3 text-left transition-colors hover:border-brand-300 hover:bg-brand-50 dark:border-gray-700 dark:bg-gray-800 dark:hover:border-brand-500/50 dark:hover:bg-gray-700">
            <span class="flex items-center gap-2">
              <span class="truncate text-sm font-medium text-gray-900 dark:text-white">{{ opt.label }}</span>
              @if (opt.isPrimary) {
                <span class="shrink-0 rounded-full bg-success-100 px-2 py-0.5 text-xs font-medium text-success-700 dark:bg-success-500/20 dark:text-success-400">
                  Principal
                </span>
              }
            </span>
            @if (opt.sublabel) {
              <span class="mt-0.5 block truncate text-xs text-gray-500 dark:text-gray-400">{{ opt.sublabel }}</span>
            }
          </button>
        }
      </div>

      <div class="mt-5 flex items-center justify-end">
        <button type="button" (click)="skipRelationChoice()"
                class="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700">
          Omitir
        </button>
      </div>
    </div>
  </div>
}